/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useApolloClient, useMutation } from '@apollo/client';

import config from 'js/app/config';
import logger from 'js/app/loggerSingleton';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser, get as userGet } from 'js/lib/user';

import { Dialog, breakpoints } from '@coursera/cds-core';
import type { EOIButtonSource, EoiSection } from '@coursera/event-pulse-types';

import * as actions from 'bundles/authentication-common/components/actions';
import { shouldShowLegalConsolidationAuthFlow } from 'bundles/authentication-common/utils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import ConfirmationSection from 'bundles/expression-of-interest/components/ConfirmationSection';
import { useEoiOptimizationsContext } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import InitialSection from 'bundles/expression-of-interest/components/InitialSection';
import LoginSection from 'bundles/expression-of-interest/components/LoginSection';
import PhoneNumberSection from 'bundles/expression-of-interest/components/PhoneNumberSection';
import SelectSubjectSection from 'bundles/expression-of-interest/components/SelectSubjectSection';
import { ACTION_QUERY_PARAMS, CONTINUE_EOI_PARAMS } from 'bundles/expression-of-interest/constants';
import { CREATE_OR_UPDATE } from 'bundles/expression-of-interest/queries/CreateOrUpdateQualificationSurveySectionResponse';
import type {
  CreateOrUpdateResponse,
  CreateOrUpdateVariables,
} from 'bundles/expression-of-interest/queries/CreateOrUpdateQualificationSurveySectionResponse';
import type {
  PreviewStartSectionResponse,
  PreviewStartSectionVariables,
} from 'bundles/expression-of-interest/queries/PreviewStartSection';
import { PREVIEW_START_SECTION } from 'bundles/expression-of-interest/queries/PreviewStartSection';
import type { ProfilePhone } from 'bundles/expression-of-interest/types';
import { useFocusTrap } from 'bundles/expression-of-interest/utils/dialogUtils';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import {
  appendUtmParametersToApplicationLink,
  clearActionQueryParamLocation,
  getActionQueryParam,
  getEoiContext,
  getEoiCurrentUrl,
  getInputfromUserProfile,
  getResponse,
  getSplitName,
} from 'bundles/expression-of-interest/utils/util';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';
import useSaveOnboardingOnSignup from 'bundles/onboarding-common/hooks/useSaveOnboardingOnSignup';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';

/**
 * Delay after dialog close animation before performing follow-up actions.
 * Must be >= 195ms (MUI's leavingScreen transition duration).
 * Used for focus restoration and section state reset.
 *
 * @see duration.leavingScreen in node_modules/@material-ui/core/styles/transitions.js
 * @see https://github.com/webedx-spark/cds/blob/main/packages/cds-core/src/Modal/useTransitionDuration.ts#L9
 */
const DIALOG_CLOSE_DELAY_MS = 200;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  productId: string;
  applicationUrl?: string | null;
  setHasSubmittedEoi?: (input: boolean) => void;
  initialSection?: SectionName;
  shouldNavigateDirectlyToApplication?: boolean;
  eoiButtonSource?: EOIButtonSource;
};

// Section management
export type SectionName = 'Initial' | 'PhoneNumber' | 'Login' | 'Confirmation' | 'SelectSubject';

// In DEGREE_HUB flows these sections navigate onward to SelectSubject, so their
// submission is not the end of the journey. Everywhere else a submission is terminal.
const DEGREE_HUB_ONWARD_SECTIONS: readonly SectionName[] = ['Initial', 'PhoneNumber'];

/**
 * Whether a submission from the given section finalizes the EOI journey. Used to
 * fire a single `submit_eoi` success per completed journey (tied to the backend
 * mutation) rather than one per funnel section. Mirrors each section's navigateNext.
 */
export const isTerminalSubmissionSection = (section: SectionName, isDegreeHub: boolean): boolean =>
  !(isDegreeHub && DEGREE_HUB_ONWARD_SECTIONS.includes(section));

const styles = {
  dialog: css`
    [role='dialog'] {
      max-height: 95vh;
      ${breakpoints.up('md')} {
        width: 584px;
      }

      label {
        margin-bottom: 0;
      }

      ${breakpoints.down('xs')} {
        max-height: unset;
      }
    }
  `,
};

/**
 * This component renders a dialog for the Expression of Interest (EOI) flow. It manages the state and logic required to navigate through different sections of the EOI form.
 *
 * @param {boolean} props.isOpen - Indicates whether the dialog is open.
 * @param {Function} props.onClose - Callback function to close the dialog.
 * @param {Function} props.onOpen - Callback function to open the dialog.
 * @param {string} props.productId - The ID of the product.
 * @param {string} [props.applicationUrl] - The URL for starting the application process.
 * @param {Function} [props.setHasSubmittedEoi] - Callback function to set the EOI submission state.
 * @param {string} [props.initialSection] - The initial section of the EOI dialog.
 * @param {boolean} [props.shouldNavigateDirectlyToApplication] - Indicates whether to navigate directly to the application
 * @param {boolean} [props.eoiButtonSource] - Indicates the source of the EOI interaction/submission
 * @returns {React.ReactElement} The rendered EoiDialog component.
 */
export const EoiDialog = (props: Props): React.ReactElement => {
  const {
    isOpen,
    onClose,
    onOpen,
    productId,
    applicationUrl,
    setHasSubmittedEoi,
    initialSection,
    shouldNavigateDirectlyToApplication,
    eoiButtonSource,
  } = props;
  const trackingProductId = productId.replace('Degree~base!~', '').replace('Degree~credentialTrack!~', '');
  const { slug, productType } = useEoiOptimizationsContext();
  const { saveOnboardingOnSignup } = useSaveOnboardingOnSignup();

  const isLoggedIn = isAuthenticatedUser();

  const { trackInteractEoi, trackSubmitEoi } = useEoiEventing(trackingProductId);

  // Router variables
  const navigate = useNavigate();
  const location = useLocation();
  const currentUrl = getEoiCurrentUrl({
    baseUrl: config.url.base,
    slug,
    pathname: location.pathname,
    search: location.search ?? '',
  });
  const eoiContext = getEoiContext(currentUrl);

  // API calls
  const apolloClient = useApolloClient();
  const [previewStartSection, { data: previewStartSectionData }] = useMutation<
    PreviewStartSectionResponse,
    PreviewStartSectionVariables
  >(PREVIEW_START_SECTION);
  const sectionId = previewStartSectionData?.QualificationSurveySectionResponse.sectionId || 'section1';
  const [createOrUpdate] = useMutation<CreateOrUpdateResponse, CreateOrUpdateVariables>(CREATE_OR_UPDATE);
  const [responseId, setResponseId] = useState<string>('');

  // Form fields
  const currentUser = userGet();
  const defaultName = getSplitName(currentUser.fullName);
  const [firstName, setFirstName] = useState<string>(defaultName.firstName || '');
  const [lastName, setLastName] = useState<string>(defaultName.lastName || '');
  const [learnerEmail, setLearnerEmail] = useState<string>(currentUser.email_address);
  const [phone, setPhone] = useState<Phone>({ phoneNumber: '' });
  const [phoneRegion, setPhoneRegion] = useState<string | undefined>();
  const [subject, setSubject] = useState<{ categoryId: string; categoryText: string } | undefined>();
  const [isPhoneLoading, setIsPhoneLoading] = useState<boolean>(true);

  const continueFrom = location.query.continueFrom;
  const isContinueFromLogin = continueFrom === CONTINUE_EOI_PARAMS.CONTINUE_FROM_LOGIN;

  const getInitialSection = (): SectionName => {
    if (isLoggedIn && firstName && learnerEmail) {
      if (isContinueFromLogin) {
        if (productType === ProductTypes.DEGREE_HUB) {
          return 'SelectSubject';
        }
        return 'Confirmation';
      }
      return 'PhoneNumber';
    }
    return 'Initial';
  };

  const [currentSection, setCurrentSection] = useState<SectionName>(getInitialSection());
  // Disable focus lock in the CDS Dialog when displaying the signup/login form with the Arkose/reCAPTCHA challenge modal.
  const customFocusTrapSections = ['Initial', 'Login'];
  const shouldUseCustomFocusTrap = customFocusTrapSections.includes(currentSection);

  const dialogRef = useRef<HTMLDivElement>(null);
  const isNewSignupRef = useRef<boolean>(false);
  // Store the element that triggered the dialog for focus restoration
  const triggerElementRef = useRef<HTMLElement | null>(null);

  // Trap the focus state within the dialog when the focus lock is disabled
  useFocusTrap(dialogRef, shouldUseCustomFocusTrap);

  // Save trigger element when dialog opens for focus restoration on close
  // useLayoutEffect ensures we capture the active element BEFORE the dialog auto-focuses
  useLayoutEffect(() => {
    if (isOpen) {
      triggerElementRef.current = document.activeElement as HTMLElement;
    }
  }, [isOpen]);

  // Open the dialog when a user comes from SSO
  useEffect(() => {
    // This can be set from an external link or from a previous login from EOI,
    const actionQueryParam = getActionQueryParam(location);
    if (actionQueryParam === ACTION_QUERY_PARAMS.EMAIL_ME_INFO) {
      onOpen();
      clearActionQueryParamLocation(location, navigate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (productId) {
      previewStartSection({ variables: { productId, input: {} } });
    }

    // Get phone number from user profile
    const fetchPhone = async () => {
      if (isLoggedIn) {
        const phoneResponse = await getInputfromUserProfile(apolloClient);
        let defaultPhone = { phoneNumber: '' } as Phone;
        if (phoneResponse.phoneNumber) {
          // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          defaultPhone = phoneResponse.phoneNumber['org.coursera.survey.question.ShortAnswerResponse'].text as Phone;
        }
        setPhone(defaultPhone as Phone);
        setIsPhoneLoading(false);
        setCurrentSection(getInitialSection());
      } else {
        setIsPhoneLoading(false);
        setPhone({
          phoneNumber: '',
        });
      }
    };
    fetchPhone();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const SectionEventKeys: Record<SectionName, EoiSection> = {
    Initial: 'initial_section',
    PhoneNumber: 'phone_number_section',
    Confirmation: 'confirmation_section',
    Login: 'login_section',
    // Qualifying Questions
    SelectSubject: 'select_subject_section',
  };

  const submitResponse = (): Promise<void | {}> => {
    const responses = getResponse(firstName, lastName, learnerEmail, phone.phoneNumber, subject);
    setHasSubmittedEoi?.(true);

    // TODO: For login EOI flow, this logic unintentionally skips the EOI submission
    // We should revist and add profile data fetch here
    if (!firstName || !lastName) {
      return Promise.resolve();
    }

    // The section currently in view when the submission was triggered. Falls back to a
    // forced `initialSection` (single-section render) when the funnel isn't in use.
    const activeSection = initialSection ?? currentSection;

    return createOrUpdate({
      variables: {
        productId,
        shouldProcessTerminalActions: true,
        responseId: responseId ? `&responseId=${responseId}` : '',
        sectionId,
        eoiContext: {
          ...eoiContext,
          ...(phoneRegion ? { countryCode: phoneRegion } : {}),
          ...(eoiButtonSource ? { eoiButtonSource } : {}),
        },
        responses,
      },
    })
      .then((response) => {
        if (response.data) {
          setResponseId(response.data.QualificationSurveySectionResponse.id);
        }
        // Fire a single `success` per completed EOI journey, only once the backend
        // mutation has actually executed and only on the terminal submission. Sections
        // no longer fire their own `success` to avoid per-section over-counting.
        if (isTerminalSubmissionSection(activeSection, productType === ProductTypes.DEGREE_HUB)) {
          trackSubmitEoi({ eoiStatus: 'success', eoiSection: SectionEventKeys[activeSection], eoiButtonSource });
        }
      })
      .catch((error) => logger.error(error));
  };

  /**
   * Handles the closing of the EOI dialog, tracks interaction, restores focus, and refreshes for new signups.
   *
   * @param eoiSection - The section from which the dialog is being closed, for tracking purposes.
   */
  const handleClose = async (eoiSection: EoiSection) => {
    trackInteractEoi({ eoiSection, eoiInteraction: 'close_form', eoiButtonSource });

    onClose();

    // Restore focus to the element that triggered the dialog (accessibility requirement)
    // Use setTimeout to ensure focus restoration happens after the dialog close animation
    setTimeout(() => {
      if (
        triggerElementRef.current &&
        typeof triggerElementRef.current.focus === 'function' &&
        document.contains(triggerElementRef.current)
      ) {
        triggerElementRef.current.focus();
      }
    }, DIALOG_CLOSE_DELAY_MS);

    // Refresh the page for newly created accounts
    if (isNewSignupRef.current) {
      await saveOnboardingOnSignup();
      actions.onSignup(undefined, { addShowTouAcceptParam: shouldShowLegalConsolidationAuthFlow() });
      isNewSignupRef.current = false;
    }
    if (currentSection === 'Login') {
      setTimeout(() => {
        setCurrentSection(getInitialSection());
      }, DIALOG_CLOSE_DELAY_MS);
    }
  };

  const shortCircuitNextStepNavigation = (callback?: () => void) => {
    if (shouldNavigateDirectlyToApplication && applicationUrl) {
      window.open(appendUtmParametersToApplicationLink(applicationUrl), '_blank', 'noopener,noreferrer');
      handleClose(SectionEventKeys[currentSection]);
    } else {
      callback?.();
    }
  };

  const Sections: Record<SectionName, React.ReactNode> = {
    Initial: (
      <InitialSection
        handleFirstNameChange={(fName: string) => setFirstName(fName)}
        handleLastNameChange={(lName: string) => setLastName(lName)}
        handleEmailChange={(email: string) => setLearnerEmail(email)}
        handlePhoneChange={(data?: ProfilePhone) => {
          if (data?.phone) {
            const phoneNumberForEoiForm = data.phone;
            setPhone(phoneNumberForEoiForm);
          }
        }}
        firstName={firstName}
        lastName={lastName}
        learnerEmail={learnerEmail}
        phone={phone}
        handleSubmit={submitResponse}
        navigateLogin={() => setCurrentSection('Login')}
        navigateSelectSubject={() => setCurrentSection('SelectSubject')}
        navigateConfirmation={() => shortCircuitNextStepNavigation(() => setCurrentSection('Confirmation'))}
        trackingProductId={trackingProductId}
        setIsNewSignup={(userSignedUp) => {
          isNewSignupRef.current = userSignedUp;
        }}
        shouldSetAutoFocus={shouldUseCustomFocusTrap}
        eoiButtonSource={eoiButtonSource}
        setRegion={setPhoneRegion}
      />
    ),
    PhoneNumber: (
      <PhoneNumberSection
        phone={phone}
        isPhoneLoading={isPhoneLoading}
        handleSubmit={submitResponse} // an initial EOI is already captured before the phone number section
        navigateSelectSubject={() => setCurrentSection('SelectSubject')}
        navigateConfirmation={() => shortCircuitNextStepNavigation(() => setCurrentSection('Confirmation'))}
        handlePhoneChange={(data?: ProfilePhone) => {
          if (data?.phone) {
            const phoneNumberForEoiForm = data.phone;
            setPhone(phoneNumberForEoiForm);
          }
        }}
        trackingProductId={trackingProductId}
        eoiButtonSource={eoiButtonSource}
        setRegion={setPhoneRegion}
      />
    ),

    Login: (
      <LoginSection
        learnerEmail={learnerEmail}
        phone={phone}
        handleEmailChange={(email: string) => setLearnerEmail(email)}
        handlePhoneChange={(data?: ProfilePhone) => {
          if (data?.phone) {
            const phoneNumberForEoiForm = data.phone;
            setPhone(phoneNumberForEoiForm);
          }
        }}
        handlePreviousSection={() => {
          setCurrentSection('Initial');
        }}
        handleSubmit={submitResponse}
        trackingProductId={trackingProductId}
        shouldSetAutoFocus={shouldUseCustomFocusTrap}
        navigateConfirmation={shouldNavigateDirectlyToApplication ? shortCircuitNextStepNavigation : undefined}
        eoiButtonSource={eoiButtonSource}
        setRegion={setPhoneRegion}
      />
    ),
    SelectSubject: (
      <SelectSubjectSection
        subject={subject}
        handleSubjectChange={(selection: { categoryId: string; categoryText: string }) => setSubject(selection)}
        handleSubmit={submitResponse}
        navigateConfirmation={() => shortCircuitNextStepNavigation(() => setCurrentSection('Confirmation'))}
        trackingProductId={trackingProductId}
        eoiButtonSource={eoiButtonSource}
      />
    ),
    Confirmation: (
      <ConfirmationSection
        email={learnerEmail}
        applicationUrl={applicationUrl}
        handleClose={() => handleClose('confirmation_section')}
        trackingProductId={trackingProductId}
      />
    ),
  };

  return (
    <Dialog
      ref={dialogRef}
      open={isOpen}
      onClose={() => handleClose(SectionEventKeys[currentSection])}
      css={styles.dialog}
      data-testid="eoi-modal"
      disableFocusLock={shouldUseCustomFocusTrap}
    >
      {initialSection && Sections[initialSection]}
      {!initialSection && Sections[currentSection]}
    </Dialog>
  );
};

const EoiDialogWithErrorBoundary = (props: Props) => (
  <ErrorBoundaryWithTagging componentName="expression-of-interest/EoiDialog">
    <EoiDialog {...props} />
  </ErrorBoundaryWithTagging>
);

export default EoiDialogWithErrorBoundary;
